import { useId } from 'react';

/**
 * The FleetKinetic mark, inline rather than an <img src="/brand/...">.
 *
 * Inline because the header renders it before anything else on the page: an
 * external SVG is a second request that resolves after first paint, and a
 * logo that pops in a beat late is the first thing a user sees go wrong.
 * The file versions in /public/brand exist for everything outside the app —
 * e-mail signatures, PDFs, the client portal's OG image, suppliers.
 *
 * Three tones, and the choice is not cosmetic:
 *
 *   metal  the gradient version. Only legible on a dark ground, and only
 *          above ~48px — below that the ten lattice strokes average into
 *          one grey smear. Use for the console header and marketing.
 *   flat   same geometry, two solid colors. This is the default for UI:
 *          predictable at any size, and it survives being rendered into
 *          a PNG by a mail client that ignores gradients.
 *   mono   currentColor. For print, engraving, and anywhere the mark has
 *          to work in one ink.
 *
 * Below 48px use <FleetKineticIcon> instead — the mark does not shrink,
 * it dissolves, so the small size is a different drawing rather than the
 * same one scaled.
 */
export function FleetKineticMark({ tone = 'flat', height = 40, title = 'FleetKinetic', className = '' }) {
  // Gradient ids are document-global. Two headers on one page (the console
  // and an embedded client-portal preview) would otherwise both point at
  // whichever definition rendered last.
  const uid = useId();
  const silver = `fk-silver-${uid}`;
  const copper = `fk-copper-${uid}`;

  const isMetal = tone === 'metal';
  const structure = isMetal ? `url(#${silver})` : tone === 'mono' ? 'currentColor' : '#C9D1DB';
  const accent = isMetal ? `url(#${copper})` : tone === 'mono' ? 'currentColor' : '#C4753A';
  // In one ink the accent can no longer be told apart by hue, so it carries
  // the hierarchy in weight instead.
  const accentWidth = tone === 'mono' ? 9 : 8;

  return (
    <svg
      viewBox="0 0 720 372"
      height={height}
      className={className}
      role="img"
      aria-label={title}
      focusable="false"
    >
      {isMetal && (
        <defs>
          {/* userSpaceOnUse is load-bearing: with the default
              objectBoundingBox the gradient is resolved per path, and a
              horizontal lattice rail has a zero-height bounding box, which
              the SVG spec says renders nothing at all. Half the lattice
              silently disappears. It also gives the whole mark one
              highlight instead of twenty. */}
          <linearGradient id={silver} gradientUnits="userSpaceOnUse" x1="60" y1="20" x2="700" y2="352">
            <stop offset="0" stopColor="#FFFFFF" />
            <stop offset="0.16" stopColor="#DDE3EA" />
            <stop offset="0.34" stopColor="#ABB6C3" />
            <stop offset="0.5" stopColor="#F6F8FB" />
            <stop offset="0.67" stopColor="#B8C2CE" />
            <stop offset="0.85" stopColor="#E4E9EF" />
            <stop offset="1" stopColor="#96A1AF" />
          </linearGradient>
          <linearGradient id={copper} gradientUnits="userSpaceOnUse" x1="90" y1="60" x2="700" y2="330">
            <stop offset="0" stopColor="#F4CBA4" />
            <stop offset="0.2" stopColor="#D08A4E" />
            <stop offset="0.42" stopColor="#AE6129" />
            <stop offset="0.6" stopColor="#F1BC86" />
            <stop offset="0.8" stopColor="#C4753A" />
            <stop offset="1" stopColor="#E9AB71" />
          </linearGradient>
        </defs>
      )}

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* Gardelė — flotilė ramybėje */}
        <g stroke={structure} strokeWidth="4.5">
          <path d="M190 20 L80 352" />
          <path d="M265 20 L155 352" />
          <path d="M340 20 L230 352" />
          <path d="M415 20 L305 352" />
          <path d="M490 20 L380 352" />
          <path d="M190 20 L490 20" />
          <path d="M162.5 103 L462.5 103" />
          <path d="M135 186 L435 186" />
          <path d="M107.5 269 L407.5 269" />
          <path d="M80 352 L380 352" />
        </g>

        {/* Vidinės bangos */}
        <g stroke={structure} strokeWidth="5.5">
          <path d="M92 268  C 220 268, 246 138, 380 136 C 512 134, 570 162, 694 184" />
          <path d="M118 160 C 240 160, 266 248, 404 246 C 528 244, 580 210, 694 188" />
        </g>

        {/* Strėlė */}
        <path stroke={structure} strokeWidth="7" d="M500 22 L700 186 L500 350 L500 288 L632 186 L500 84 Z" />

        {/* Varinės gijos — tai, kas juda pirmyn */}
        <g stroke={accent} strokeWidth={accentWidth}>
          <path d="M96 318  C 216 318, 244 96,  386 94  C 520 92,  578 148, 696 184" />
          <path d="M110 130 C 230 130, 258 286, 400 284 C 530 282, 580 224, 696 188" />
          <path d="M140 208 C 300 208, 350 186, 470 186 L 696 186" />
        </g>
      </g>
    </svg>
  );
}

/**
 * The small-size drawing. Not the mark scaled down — the lattice is dropped
 * entirely, because at 24px it contributes texture and no information. What
 * survives is what stays recognizable at 16px: two crossing strands entering
 * the arrow.
 *
 * `plate` draws the navy ground. Keep it on for browser tabs and app icons,
 * where the surrounding color is out of our hands and silver on a light tab
 * bar disappears; turn it off when the icon sits on a surface we control.
 */
export function FleetKineticIcon({ size = 24, plate = true, title = 'FleetKinetic', className = '' }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title}
      focusable="false"
    >
      {plate && <rect width="64" height="64" rx="14" fill="#0B1220" />}
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M31 13 L54 32 L31 51 L31 43.5 L44.5 32 L31 20.5 Z" stroke="#C9D1DB" strokeWidth="2.6" />
        <path d="M9 45 C 20 45, 20 18.5, 31.5 18.5 C 41 18.5, 45.5 27, 52.6 31.2" stroke="#C4753A" strokeWidth="4" />
        <path d="M9 19 C 20 19, 20 45.5, 31.5 45.5 C 41 45.5, 45.5 37, 52.6 32.8" stroke="#C9D1DB" strokeWidth="4" />
      </g>
    </svg>
  );
}
